import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import {
  OtherVaccinationTableHeaderColumn,
  RabiesVaccinationTableHeaderColumn,
} from "./enums/VaccinationTableHeaderColumns";
import { VaccinationCategory } from "../../../vaccination/enums/VaccinationCategory";

export const TrimmedTableCell = ({ value, maxLength = 30, testId }) => {
  const text = value ? String(value) : "-";
  const shouldTrim = text.length > maxLength;

  const displayText = shouldTrim ? `${text.substring(0, maxLength)}...` : text;

  // Return the cell wrapped in Tooltip if trimmed
  return shouldTrim ? (
    <Tooltip title={text} placement="top">
      <TableCell data-testid={testId}>{displayText}</TableCell>
    </Tooltip>
  ) : (
    <TableCell data-testid={testId}>{displayText}</TableCell>
  );
};

function RabiesVaccinationTable({ vaccinations }) {
  const { t } = useTranslation();

  const rabiesVaccinations = vaccinations.filter(
    (v) => v.vaccinationCategory === VaccinationCategory.RABIES
  );

  // Return null if no rabies vaccinations exist
  if (rabiesVaccinations.length === 0) return null;

  return (
    <Paper elevation={2} sx={{ padding: 3, borderRadius: 2, mb: 5 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <VaccinesIcon color="error" sx={{ mr: 1 }} />
        <Typography variant="h6" fontWeight={"light"}>
          {t("RabiesVaccinations")}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <TableContainer
        sx={{
          minHeight: 150,
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="rabies vaccination table">
          <TableHead>
            <TableRow>
              {RabiesVaccinationTableHeaderColumn.map((headerKey) => (
                <TableCell key={headerKey}>{t(headerKey)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rabiesVaccinations.map((vaccination, index) => (
              <TableRow
                key={index}
                data-testid={`vaccination.table.rabies.row.${index}`}
              >
                <TrimmedTableCell
                  value={vaccination.name}
                  testId={`vaccination.table.rabies.row.${index}.name`}
                />
                <TrimmedTableCell
                  value={vaccination.serialNumber}
                  testId={`vaccination.table.rabies.row.${index}.serial-number`}
                />
                <TableCell
                  data-testid={`vaccination.table.rabies.row.${index}.date-of-application`}
                >
                  {vaccination.dateOfApplication || "-"}
                </TableCell>
                <TableCell
                  data-testid={`vaccination.table.rabies.row.${index}.expiration-date`}
                >
                  {vaccination.expirationDate || "-"}
                </TableCell>
                <TableCell
                  data-testid={`vaccination.table.rabies.row.${index}.immunity-period`}
                >
                  {vaccination.immunityPeriod
                    ? `${vaccination.immunityPeriod} ${
                        Number(vaccination.immunityPeriod) === 1
                          ? t("SingleYear")
                          : t("PluralYear")
                      }`
                    : "-"}
                </TableCell>
                <TableCell
                  data-testid={`vaccination.table.rabies.row.${index}.is-first`}
                >
                  {vaccination.isFirstRabiesVaccine ? t("Yes") : t("No")}
                </TableCell>
                <TrimmedTableCell
                  value={vaccination.description}
                  testId={`vaccination.table.rabies.row.${index}.description`}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function OtherVaccinationTable({ vaccinations }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const otherVaccinations = vaccinations.filter(
    (v) => v.vaccinationCategory === VaccinationCategory.OTHER
  );

  // Return null if no other vaccinations exist
  if (otherVaccinations.length === 0) return null;

  const paginatedVaccinations = otherVaccinations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper elevation={2} sx={{ padding: 3, borderRadius: 2, mb: 5 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <VaccinesIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6" fontWeight={"light"}>
          {t("OtherVaccinations")}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <TableContainer
        sx={{
          minHeight: 150,
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="other vaccination table">
          <TableHead>
            <TableRow>
              {OtherVaccinationTableHeaderColumn.map((headerKey) => (
                <TableCell key={headerKey}>{t(headerKey)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedVaccinations.map((vaccination, index) => (
              <TableRow
                key={index}
                data-testid={`vaccination.table.other.row.${index}`}
              >
                <TrimmedTableCell
                  value={vaccination.name}
                  testId={`vaccination.table.other.row.${index}.name`}
                />
                <TrimmedTableCell
                  value={vaccination.lot}
                  testId={`vaccination.table.other.row.${index}.lot`}
                />
                <TableCell
                  data-testid={`vaccination.table.other.row.${index}.powder-expiration`}
                >
                  {vaccination.powderExpirationDate || "-"}
                </TableCell>
                <TableCell
                  data-testid={`vaccination.table.other.row.${index}.solvent-expiration`}
                >
                  {vaccination.solventExpirationDate || "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {otherVaccinations.length > rowsPerPage && (
        <TablePagination
          rowsPerPageOptions={[5]}
          component="div"
          labelRowsPerPage={t("RowsPerPage")}
          count={vaccinations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          backIconButtonProps={{
            title: t("PreviousPage"),
            "aria-label": t("PreviousPage"),
          }}
          nextIconButtonProps={{
            title: t("NextPage"),
            "aria-label": t("NextPage"),
          }}
        />
      )}
    </Paper>
  );
}
//For showing legacy Vaccinations before the refactor that didn't have a vaccination category.
//This should only be shown when showing a older vaccination
function LegacyVaccinationTable({ vaccinations }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(5);

  // Filter: Show only if category is null, undefined, or empty
  const legacyVaccinations = vaccinations.filter(
    (v) => !v.vaccinationCategory
  );

  if (legacyVaccinations.length === 0) return null;

  const paginatedVaccinations = legacyVaccinations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper elevation={2} sx={{ padding: 3, borderRadius: 2, mb: 5 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={"light"}>
          {t("VaccinationDetails")} 
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <TableContainer sx={{ minHeight: 150, overflow: "auto" }}>
        <Table sx={{ minWidth: 650 }} aria-label="legacy vaccination table">
          <TableHead>
            <TableRow>
              <TableCell>{t("Title")}</TableCell>
              <TableCell>{t("SerialNumber")}</TableCell>
              <TableCell>{t("ExpirationDate")}</TableCell>
              <TableCell>{t("DateOfApplication")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedVaccinations.map((vaccination, index) => (
              <TableRow key={index}>
                <TrimmedTableCell value={vaccination.name} />
                <TrimmedTableCell value={vaccination.serialNumber} />
                <TableCell>{vaccination.expirationDate || "-"}</TableCell>
                <TableCell>{vaccination.dateOfApplication || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {legacyVaccinations.length > rowsPerPage && (
        <TablePagination
          rowsPerPageOptions={[5]}
          component="div"
          labelRowsPerPage={t("RowsPerPage")}
          count={legacyVaccinations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          backIconButtonProps={{
            title: t("PreviousPage"),
            "aria-label": t("PreviousPage"),
          }}
          nextIconButtonProps={{
            title: t("NextPage"),
            "aria-label": t("NextPage"),
          }}
        />
      )}
    </Paper>
  );
}

export default function VaccinationTable({ vaccinations }) {
  return (
    <>
      {vaccinations && vaccinations.length > 0 && (
        <Box>
          <OtherVaccinationTable vaccinations={vaccinations} />
          <RabiesVaccinationTable vaccinations={vaccinations} />
          <LegacyVaccinationTable vaccinations={vaccinations}/>
        </Box>
      )}
    </>
  );
}